import React, { useCallback, useContext, useState } from 'react';
import { Redirect } from 'react-router-dom';
import { ThreeDSecureAction } from '@recurly/react-recurly';
import StripeElementsProvider from '../../../../lib/StripeElementsProvider';
import StripePaymentForm, { StripeAddressValue, StripeCardDetails } from '../../../../components/StripeCheckout';
import FiRecurlyProvider from '../../../../components/FiRecurlyProvider';
import CheckoutContext from '../../../../lib/CheckoutContext';
import { CartPricingProvider } from '../../../../contexts/CartPricingContext';
import { useLocale } from '../../../../contexts/LocaleContext';
import { priceCartForPreview } from '../../../../lib/pricing';
import { isExpeditedShippingAvailable } from '../../../../lib/shipping';
import { getFiGrowthBook } from '../../../../lib/growthbook';
import useCheckoutPaths from '../../../../hooks/useCheckoutPaths';
import * as types from '../../../../types';
import StepContainer from '../../components/StepContainer';
import CheckoutSection from '../../../../models/CheckoutSection';
import useStripeCheckout from './useStripeCheckout';

function StripeCheckoutContent() {
  const { currency } = useLocale();
  const {
    submitCheckout,
    submitting,
    error,
    threeDSecureActionToken,
    threeDSecurePending,
    onThreeDSecureToken,
    onThreeDSecureError,
  } = useStripeCheckout();

  const [shippingCode, setShippingCode] = useState<types.ShippingCode | undefined>(undefined);
  const [address, setAddress] = useState<StripeAddressValue | undefined>(undefined);
  const [email, setEmail] = useState('');

  const canExpedite = address
    ? isExpeditedShippingAvailable({
        state: address.address.state,
        line1: address.address.line1,
        line2: address.address.line2,
      })
    : true;

  const handleConfirmationToken = useCallback(
    (tokenId: string, cardDetails: StripeCardDetails) => {
      if (!address || !shippingCode) {
        return;
      }
      void submitCheckout({ tokenId, email, address, shippingCode, cardDetails });
    },
    [submitCheckout, email, address, shippingCode],
  );

  const paymentForm = (
    <StripePaymentForm
      onConfirmationToken={handleConfirmationToken}
      onAddressChange={setAddress}
      onEmailChange={setEmail}
      onShippingCodeChange={setShippingCode}
      anyPreorder={false}
      canExpedite={canExpedite}
      selectedShippingCode={shippingCode}
      submitting={submitting || threeDSecurePending}
      serverError={error}
    />
  );

  return (
    <StripeElementsProvider mode="setup" currency={currency}>
      {paymentForm}
      {threeDSecureActionToken && (
        <FiRecurlyProvider>
          <ThreeDSecureAction
            actionTokenId={threeDSecureActionToken}
            onToken={(token) => void onThreeDSecureToken(token)}
            onError={onThreeDSecureError}
          />
        </FiRecurlyProvider>
      )}
    </StripeElementsProvider>
  );
}

export default function StripeCheckoutPage() {
  const { cart } = useContext(CheckoutContext);
  const checkoutPaths = useCheckoutPaths();
  const fetchPricing = useCallback(() => priceCartForPreview(cart), [cart]);

  const useStripeCheckoutFlag = getFiGrowthBook().getFeatureValue('stripe-elements-checkout', false);
  if (!useStripeCheckoutFlag) {
    return <Redirect to={checkoutPaths.Shipping} />;
  }

  return (
    <CartPricingProvider fetchPricing={fetchPricing}>
      <StepContainer checkoutSection={CheckoutSection.payment}>
        <StripeCheckoutContent />
      </StepContainer>
    </CartPricingProvider>
  );
}
